import { NOVITA_API_URL } from './config';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 12;

const nullableString = (value) =>
  typeof value === 'string' && value.trim() ? value : null;

const stringArray = (value) =>
  Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];

const formatDateParam = (value) => {
  if (!value) return null;
  return value instanceof Date ? value.toISOString() : value;
};

export const buildNovitaApiUrl = ({
  endpoint = NOVITA_API_URL,
  page = DEFAULT_PAGE,
  pageSize = DEFAULT_PAGE_SIZE,
  from,
  to,
} = {}) => {
  const url = new URL(endpoint);
  url.searchParams.set('page', String(page));
  url.searchParams.set('pageSize', String(pageSize));

  const fromParam = formatDateParam(from);
  if (fromParam) url.searchParams.set('from', fromParam);

  const toParam = formatDateParam(to);
  if (toParam) url.searchParams.set('to', toParam);

  return url.toString();
};

const assertObject = (value, message) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(message);
  }
};

const parseNovitaItem = (item) => {
  assertObject(item, 'Payload novita non valido');

  if (
    typeof item.slug !== 'string' ||
    typeof item.title !== 'string' ||
    typeof item.date !== 'string' ||
    typeof item.url !== 'string'
  ) {
    throw new Error('Payload novita non valido');
  }

  return {
    id: nullableString(item.id) || item.slug,
    slug: item.slug,
    title: item.title,
    excerpt: nullableString(item.excerpt) || '',
    publishedAt: item.date,
    product: nullableString(item.product),
    label: nullableString(item.label),
    tags: stringArray(item.tags),
    modules: stringArray(item.modules),
    cover: nullableString(item.cover),
    url: item.url,
    embedUrl: nullableString(item.embedUrl),
    categories: stringArray(item.tags),
  };
};

export const parseNovitaResponse = (payload) => {
  assertObject(payload, 'Payload novita non valido');
  assertObject(payload.pagination, 'Payload novita non valido');

  if (!Array.isArray(payload.items)) {
    throw new Error('Payload novita non valido');
  }

  return {
    items: payload.items.map(parseNovitaItem),
    filters: {
      from: nullableString(payload.filters?.from),
      to: nullableString(payload.filters?.to),
    },
    pagination: {
      page: Number(payload.pagination.page) || 1,
      pageSize: Number(payload.pagination.pageSize) || payload.items.length,
      totalItems: Number(payload.pagination.totalItems) || payload.items.length,
      pageCount: Number(payload.pagination.pageCount) || 1,
      hasPreviousPage: Boolean(payload.pagination.hasPreviousPage),
      hasNextPage: Boolean(payload.pagination.hasNextPage),
    },
    links: {
      self: nullableString(payload.links?.self),
      first: nullableString(payload.links?.first),
      previous: nullableString(payload.links?.previous),
      next: nullableString(payload.links?.next),
      last: nullableString(payload.links?.last),
    },
  };
};

export const fetchNovita = async (options = {}) => {
  const response = await fetch(buildNovitaApiUrl(options), {
    signal: options.signal,
  });

  if (!response.ok) {
    throw new Error(`Novita request failed with status ${response.status}`);
  }

  return parseNovitaResponse(await response.json());
};
